import { praktijkvoorbeelden, type Praktijkvoorbeeld } from "@/data/praktijkvoorbeelden";
import { ArrowRight, ArrowLeft, BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import dienstenHeroBg from "@/assets/diensten-hero-bg.jpg";

const algemeen = praktijkvoorbeelden.filter((p) => p.category === "algemeen");
const zorg = praktijkvoorbeelden.filter((p) => p.category === "zorg");

const CardGrid = ({ items, label }: { items: Praktijkvoorbeeld[]; label: string }) => (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((voorbeeld, index) => {
              const Icon = voorbeeld.icon;
              return (
                <motion.div
                  key={voorbeeld.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                >
                  <Link
                    to={`/praktijkvoorbeelden/${voorbeeld.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover"
                  >
                    {/* Hero image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={voorbeeld.heroImage}
                        alt={voorbeeld.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy" decoding="async"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute bottom-4 left-4 flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <span className="absolute right-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-xs font-semibold text-primary-foreground">
                        {label}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="flex flex-1 flex-col p-6">
                      <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                        {voorbeeld.subtitle}
                      </p>
                      <h2 className="mt-1 text-xl font-bold">
                        {voorbeeld.title}
                      </h2>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                        {voorbeeld.description}
                      </p>
                      <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-primary">
                        Lees meer
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
);

const PraktijkvoorbeeldenOverzicht = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden">
        <img
          src={dienstenHeroBg}
          alt="IT-professional aan het werk"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/25" />
        <div className="container relative z-10 flex min-h-[420px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar home
            </Link>

            <motion.div
              className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/20 bg-white/15 backdrop-blur-sm"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.05 }}
            >
              <BookOpen className="h-7 w-7 text-white" />
            </motion.div>

            <motion.h1
              className="text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-5xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Praktijk<span className="text-gradient">voorbeelden</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Herkenbare IT-situaties en hoe wij ze oplossen. Geen vage theorie,
              maar concrete voorbeelden uit de praktijk.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="py-16 md:py-24">
        <div className="container">
          <CardGrid items={algemeen} label="Praktijkvoorbeeld" />
        </div>
      </section>

      {/* Zorg */}
      <section id="zorg" className="scroll-mt-24 border-t border-border py-16 md:py-24">
        <div className="container">
          <h2 className="mb-10 text-3xl font-bold md:text-4xl">Zorg</h2>
          <CardGrid items={zorg} label="Zorg" />
        </div>
      </section>
    </>
  );
};

export default PraktijkvoorbeeldenOverzicht;
